/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';

export default class HeadingsController extends Controller {
  @service
  session;

  @service
  router;

  @service
  store;

  @service
  paperToaster;

  @tracked
  headingName = '';

  @action
  setHeadingName(value) {
    this.headingName = value;
  }

  @tracked
  headingKey = '';

  @action
  setHeadingKey(value) {
    this.headingKey = value;
  }

  @tracked
  headingSequence = 0;

  @action
  setHeadingSequence(value) {
    this.headingSequence = value;
  }

  @tracked
  headingId = '';

  @tracked
  headingDialogOpen = false;

  @tracked
  saveInProgress = false;

  @tracked
  deleteHeadingDialogOpen = false;

  @tracked
  deleteInProgress = false;

  @tracked
  errorMessage = '';

  // toggleDialog opens the heading dialog.
  toggleDialog() {
    this.errorMessage = '';
    this.toggleProperty('headingDialogOpen');
    if (!this.headingDialogOpen) {
      this.resetForm();
    }
  }

  @action
  toggleHeadingDialog() {
    this.toggleDialog();
  }

  @action
  editHeading(id) {
    this.store.findRecord('school-options/heading', id).then((heading) => {
      this.headingName = heading.get('name');
      this.headingKey = heading.get('key');
      this.headingSequence = heading.get('sequence');
    });
    this.headingId = id;
    this.toggleDialog();
  }

  @action
  saveHeading() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let promise;
    let action = 'create';

    if (isEmpty(this.headingId)) {
      promise = new Promise((resolve) => {
        let record = this.store.createRecord('school-options/heading', {
          name: this.headingName,
          key: this.headingKey,
          sequence: this.headingSequence,
          groupId: this.get('model.group'),
        });

        resolve(record);
      });
    } else {
      action = 'update';
      promise = new Promise((resolve) => {
        const id = this.headingId;
        this.store.findRecord('school-options/heading', id).then((heading) => {
          heading.set('name', this.headingName);
          heading.set('key', this.headingKey);
          heading.set('sequence', this.headingSequence);

          resolve(heading);
        });
      });
    }

    promise.then((record) => {
      return record
        .save()
        .then(() => {
          this.headingDialogOpen = false;
          this.resetForm();
        })
        .catch((err) => {
          this.handleError(err, action);
          record.rollbackAttributes();
        })
        .then(() => {
          this.saveInProgress = false;
          if (action === 'create') {
            this.router.refresh('school-options.headings');
          }
        });
    });
  }

  @action
  deleteHeading(id) {
    this.errorMessage = '';
    this.store.findRecord('school-options/heading', id).then((heading) => {
      this.headingName = heading.get('name');
    });
    this.headingId = id;
    this.deleteHeadingDialogOpen = true;
  }

  @action
  confirmDelete(confirmed) {
    return new Promise((resolve, reject) => {
      if (confirmed) {
        // Deletion confirmed; actually delete the record.
        this.deleteInProgress = true;

        // Grab record from local store - no GET request, as that causes weirdness.
        // We only need the ID to issue the DELETE anyway, which can't change, so no need to use findRecord.
        let heading = this.store.peekRecord(
          'school-options/heading',
          this.headingId,
        );

        // Delete record (locally + pushed to server).
        return heading
          .destroyRecord()
          .then(resolve)
          .catch((err) => {
            // Remove 'deleted' flag so that entity can be interacted with again.
            heading.rollbackAttributes();
            reject(err);
          });
      } else {
        resolve();
      }
    })
      .then(() => {
        // Tidy up / close dialog.
        this.deleteHeadingDialogOpen = false;
        this.headingId = '';
        this.headingName = '';
      })
      .catch((err) => {
        this.handleError(err, 'delete');
      })
      .then(() => {
        this.deleteInProgress = false;
      });
  }

  handleError(err) {
    if (!isEmpty(err.errors.msg)) {
      this.errorMessage = err.errors.msg;
    } else {
      this.errorMessage = err.message;
    }
  }

  setErrorMessage(code, action) {
    var message = 'An unknown error occurred.  Please try again.';
    const codes = {
      400: 'Sorry, your request was invalid.  Please check all details below, and try again.',
      403: {
        create:
          'Sorry, you are not authorised to create a new user.  Please check that you are logged in as the correct user, and try again.',
        update:
          'Sorry, you are not authorised to edit this user.  Please check that you are logged in as the correct user, and try again.',
        delete:
          'Sorry, you are not authorised to delete this user.  Please check that you are logged in as the correct user, and try again.',
      },
      409: 'A user already exists with that username.  Please double-check the details, or find and update the existing user.',
    };

    if (!isEmpty(codes[code][action])) {
      message = codes[code][action];
    } else if (!isEmpty(codes[code])) {
      message = codes[code];
    }

    this.errorMessage = message;
  }

  resetForm() {
    this.headingId = '';
    this.headingName = '';
    this.headingKey = '';
    this.headingSequence = '';
  }
}
